import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { submitQuote, getVehicles } from "../lib/data";
import { useSettings } from "../lib/settings";

const inputCls =
  "w-full bg-[#FAFBF6] border border-[#B09060]/25 rounded-sm px-4 py-3 text-sm text-[#404040] placeholder-[#9B9B8F] focus:outline-none focus:border-[#B09060] focus:ring-1 focus:ring-[#B09060]/40 transition-colors";
const labelCls =
  "block text-xs uppercase tracking-[0.2em] font-semibold text-[#8F7648] mb-1.5";

export function QuoteForm({ formType = "teklif", defaultVehicle = "" }) {
  const CONTACT = useSettings();
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    phone: "",
    email: "",
    vehicle_interest: defaultVehicle,
    message: "",
  });
  const [vehicles, setVehicles] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getVehicles().then(setVehicles);
  }, []);

  useEffect(() => {
    if (defaultVehicle) setForm((f) => ({ ...f, vehicle_interest: defaultVehicle }));
  }, [defaultVehicle]);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  async function onSubmit(e) {
    e.preventDefault();
    setSending(true);
    const { ok, error } = await submitQuote({ ...form, form_type: formType });
    setSending(false);
    if (ok) {
      toast.success("Teklif talebiniz alındı. Ekibimiz en kısa sürede size dönüş yapacak.");
      setForm({
        company_name: "",
        contact_name: "",
        phone: "",
        email: "",
        vehicle_interest: defaultVehicle,
        message: "",
      });
    } else {
      toast.error(
        `Talep gönderilemedi (${error || "bağlantı hatası"}). Lütfen WhatsApp hattımızdan bize ulaşın.`
      );
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quote-form"
      className="text-left bg-[#FAFBF6] border border-[#B09060]/20 rounded-none p-8 shadow-[0_4px_20px_rgba(176,144,96,0.05)]"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls} htmlFor="company_name">Firma Adı *</label>
          <input
            id="company_name"
            data-testid="quote-company-input"
            name="company_name"
            required
            value={form.company_name}
            onChange={set}
            placeholder="Şirketinizin adı"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="contact_name">Yetkili Adı Soyadı *</label>
          <input
            id="contact_name"
            data-testid="quote-name-input"
            name="contact_name"
            required
            value={form.contact_name}
            onChange={set}
            placeholder="Ad Soyad"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="phone">Telefon *</label>
          <input
            id="phone"
            data-testid="quote-phone-input"
            name="phone"
            type="tel"
            required
            value={form.phone}
            onChange={set}
            placeholder="05XX XXX XX XX"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="email">E-posta</label>
          <input
            id="email"
            data-testid="quote-email-input"
            name="email"
            type="email"
            value={form.email}
            onChange={set}
            placeholder="ornek@sirket.com"
            className={inputCls}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="vehicle_interest">İlgilendiğiniz Araç</label>
          <select
            id="vehicle_interest"
            data-testid="quote-vehicle-select"
            name="vehicle_interest"
            value={form.vehicle_interest}
            onChange={set}
            className={inputCls}
          >
            <option value="">Genel filo teklifi</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.name}>
                {v.name}
              </option>
            ))}
            <option value="Kurumsal Filo">Kurumsal filo (çoklu araç)</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="message">Mesajınız</label>
          <textarea
            id="message"
            data-testid="quote-message-input"
            name="message"
            rows={4}
            value={form.message}
            onChange={set}
            placeholder="Araç adedi, kiralama süresi ve özel ihtiyaçlarınız..."
            className={inputCls}
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button
          type="submit"
          disabled={sending}
          data-testid="quote-submit-button"
          className="flex-1 py-3.5 px-6 rounded-sm bg-[#B09060] text-[#FAFBF6] font-semibold text-xs uppercase tracking-[0.15em] hover:bg-[#8F7648] hover:-translate-y-px transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" strokeWidth={1.5} />
          {sending ? "Gönderiliyor..." : "Teklif Talebini Gönder"}
        </button>
        <a
          href={CONTACT.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="quote-whatsapp-button"
          className="flex items-center justify-center py-3.5 px-6 rounded-sm border border-[#404040]/25 text-[#404040] font-semibold text-xs uppercase tracking-[0.15em] hover:border-[#B09060] hover:text-[#8F7648] transition-colors"
        >
          WhatsApp Hattı
        </a>
      </div>
    </form>
  );
}
